import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { formatDateLabel, formatRupiah } from "./gross-profit-utils";

import type { GrossProfitDailyBreakdown } from "@artisancode/api-types";

interface GrossProfitChartProps {
  data: GrossProfitDailyBreakdown[];
}

export function GrossProfitChart({ data }: GrossProfitChartProps) {
  if (!data.length) {
    return null;
  }

  const chartData = data.map((item) => ({
    date: item.date,
    formattedDate: formatDateLabel(item.date),
    revenue: item.revenue,
    cogsTotal: item.cogsTotal,
    grossProfit: item.grossProfit,
    margin: item.margin,
  }));

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">
          Tren Harian (Omzet, HPP, & Laba Kotor)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[280px] w-full pt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                opacity={0.25}
                vertical={false}
              />
              <XAxis
                dataKey="formattedDate"
                tickLine={false}
                axisLine={false}
                fontSize={12}
                dy={8}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                fontSize={12}
                tickFormatter={(val: number) => {
                  if (val >= 1_000_000)
                    return `${(val / 1_000_000).toFixed(1)}jt`;
                  if (val >= 1_000) return `${(val / 1_000).toFixed(0)}rb`;
                  return `${val}`;
                }}
              />
              <Tooltip
                formatter={(value: unknown, name: unknown) => {
                  const num = Number(value) || 0;
                  const labelMap: Record<string, string> = {
                    revenue: "Omzet",
                    cogsTotal: "Total HPP",
                    grossProfit: "Laba Kotor",
                  };
                  return [
                    formatRupiah(num),
                    labelMap[String(name)] || String(name),
                  ];
                }}
                labelFormatter={(label: unknown) => `Tanggal: ${label}`}
                contentStyle={{
                  backgroundColor: "var(--background)",
                  borderColor: "var(--border)",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
              />
              <Legend
                verticalAlign="top"
                align="right"
                height={36}
                formatter={(val) => {
                  const labelMap: Record<string, string> = {
                    revenue: "Omzet",
                    cogsTotal: "HPP",
                    grossProfit: "Laba Kotor",
                  };
                  return (
                    <span className="text-xs font-medium text-foreground">
                      {labelMap[val] || val}
                    </span>
                  );
                }}
              />
              <Bar
                dataKey="revenue"
                name="revenue"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="cogsTotal"
                name="cogsTotal"
                fill="#f59e0b"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="grossProfit"
                name="grossProfit"
                fill="#10b981"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
